import * as yup from "yup";
import { Images } from "../../../../config/images";
import TopImage from "../../common/TopImage";
import CustomInput from "../../../common/CustomInput";
import "./Teacher.css";
import { useCreateTeacher } from "../../../../hooks/school/teacher/useCreateTeacher";
import { useRef, useState, useEffect } from "react";
import { Form, Formik } from "formik";
import { Button } from "react-bootstrap";
import SuccessModal from "../../../common/SuccessModal";
import ImportModal from "../../../common/ImportModal";
import { useImportTeachers } from "../../../../hooks/import/school/useImportTeachers";
import DownloadTemplate from "../../../common/DownloadTemplate";
export const teacherValidationSchema = yup.object().shape({
  firstName: yup
    .string()
    .required("First Name is required")
    .min(3, "First Name must be atleast 3 characters")
    .max(150, "First Name must be at most 150 characters")
    .matches(/^[A-Za-z ]+$/, "First name must contain only letters"),
  lastName: yup
    .string()
    .required("Last Name is required")
    .min(3, "Last Name must be atleast 3 characters")
    .max(150, "Last Name must be at most 150 characters")
    .matches(/^[A-Za-z ]+$/, "Last name must contain only letters"),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required")
    .max(150, "Email must be at most 150 characters"),
});

const CreateTeacher = () => {
  const firstNameRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportSuccessModal, setShowImportSuccessModal] = useState(false);
  const { loading, createTeacher, error } = useCreateTeacher();
  const {
    importTeachers,
    loading: importLoading,
    error: importError,
  } = useImportTeachers();

  useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
  };

  const handleSubmit = async (values, { resetForm }) => {
    const teachers = [];
    teachers.push({
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email,
    });
    await createTeacher(teachers, () => {
      resetForm();
      setShowModal(true);
    });
  };

  return (
    <div>
      <TopImage imageSrc={Images.TopImage} text="Add Teachers" />

      <div className="d-flex justify-content-end align-items-center gap-3 pt-3">
        <div className="w-10">
          <Button
            className="custom-primary-button"
            onClick={() => setShowImportModal(true)}
          >
            Import
          </Button>
        </div>

        <DownloadTemplate type="teachers" buttonMessage="Download Template" />
      </div>

      <div className="custom-card mt-4">
        <Formik
          initialValues={initialValues}
          validationSchema={teacherValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="d-flex flex-column gap-3">
              <div className="row">
                <div className="col-md-6">
                  <CustomInput
                    label="First Name"
                    name="firstName"
                    type="text"
                    placeholder="Enter first name"
                    className="custom-primary-input"
                    layout="stacked"
                    ref={firstNameRef}
                  />
                </div>
                <div className="col-md-6">
                  <CustomInput
                    label="Last Name"
                    name="lastName"
                    type="text"
                    placeholder="Enter last name"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>

                <div className="col-md-12 mt-4">
                  <CustomInput
                    label="Email"
                    name="email"
                    type="email"
                    placeholder="Enter email address"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>
              </div>

              <div>
                {error && <div className="custom-error pb-2">{error}</div>}

                <Button
                  type="submit"
                  className="custom-primary-button"
                  disabled={isSubmitting || loading}
                >
                  {isSubmitting || loading ? "Creating..." : "Add"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <SuccessModal
        showModal={showModal}
        handleClose={setShowModal}
        title="Teacher Added Successfully!"
        message="Teacher has been added to your school’s dashboard. They can now create
          classes, assign bundles, and track student progress."
        secondaryButtonText="Teacher List"
        primaryButtonText="Add Another"
        secondaryButtonLink={"/school-admin/teachers?refresh=1"}
      />
      <ImportModal
        show={showImportModal}
        handleClose={() => setShowImportModal(false)}
        title="Import Teachers"
        description="Upload a CSV or Excel file to import teachers into your school."
        onImport={async (file) => {
          await importTeachers(file, () => {
            setShowImportModal(false);
            setShowImportSuccessModal(true);
          });
        }}
        loading={importLoading}
        error={importError}
      />
      <SuccessModal
        type="success"
        showModal={showImportSuccessModal}
        handleClose={() => setShowImportSuccessModal(false)}
        title="Import Started!"
        message="Your import has been started. Once imported, we will send the email to you."
        primaryButtonText="OK"
        onPrimaryClick={() => setShowImportSuccessModal(false)}
      />
    </div>
  );
};

export default CreateTeacher;
